// ============================================================================
// HomePage — หน้าแรกฝั่งผู้ใช้ทั่วไป (เปิดสาธารณะ ไม่ต้อง login)
//   /overview                       แสดงห้องของ "ทุกโครงการ"
//   /overview?project=<project_id>  กรองเฉพาะโครงการเดียว
//
// โครงหน้า (เรียงตามลำดับสายตา)
//   1. เมนูบน               HomeNavbar
//   2. ภาพหลัก + ค้นหา      HomeHero
//   3. ทางลัดค้นหา          HomeCategories
//   4. แบนเนอร์             HomePromo
//   5. ห้องประกาศ           HomeFeatured
//   6. โครงการ + รีวิว      HomeProjects
//   7. จุดเด่น + ตัวเลข      HomeAbout
//   8. คำถามที่พบบ่อย       HomeFaq
//   9. แถบติดต่อ            HomeCta
//  10. ฟุตเตอร์             HomeFooter
//
// ★ ห้องขึ้นหน้านี้ก็ต่อเมื่อผู้ดูแลติ๊ก "เผยแพร่" เท่านั้น
//   และ API ไม่ส่งชื่อ/เบอร์เจ้าของห้องออกมาเลย (ดู sale-public.response.js)
//
// ★ ตัวกรองทุกตัวอยู่ใน URL (?project=&listing_type=&budget=&q=&status=&sort=)
//   กดเข้าห้องแล้วกด back ตัวกรองต้องกลับมาครบ และก๊อปลิงก์ส่งต่อได้ผลเดียวกัน
//   เปลี่ยนตัวกรองใช้ navigate(..., { replace }) — ไม่สร้างประวัติทุกครั้งที่เลือก dropdown
// ============================================================================

import React, { Component } from "react";

import { loading } from "../../components/loading.js";
import { fetchProjects, fetchListings, fetchPageContent, submitInquiry } from "../../components/sale-api.js";
import { stripJuristic } from "../../components/text-sanitize.js";

import HomeNavbar from "./components/HomeNavbar.js";
import HomeHero from "./components/HomeHero.js";
import HomeCategories from "./components/HomeCategories.js";
import HomePromo from "./components/HomePromo.js";
import HomeFeatured from "./components/HomeFeatured.js";
import HomeProjects from "./components/HomeProjects.js";
import HomeAbout from "./components/HomeAbout.js";
import HomeFaq from "./components/HomeFaq.js";
import HomeCta from "./components/HomeCta.js";
import HomeFooter from "./components/HomeFooter.js";
import HomeInquiry from "./components/HomeInquiry.js";
import ICON from "./icons.js";

import "../../assets/css/homepage.css";

// ช่วงราคา — แยกชุดกันระหว่างซื้อ (ราคาขาย) กับเช่า (ค่าเช่าต่อเดือน)
const SALE_BUDGETS = [
    { value: "", label: "ทุกช่วงราคา" },
    { value: "0-1500000", label: "ไม่เกิน 1.5 ล้าน" },
    { value: "1500001-2500000", label: "1.5 - 2.5 ล้าน" },
    { value: "2500001-4000000", label: "2.5 - 4 ล้าน" },
    { value: "4000001-", label: "มากกว่า 4 ล้าน" },
];

const RENT_BUDGETS = [
    { value: "", label: "ทุกช่วงราคา" },
    { value: "0-6000", label: "ไม่เกิน 6,000 บาท" },
    { value: "6001-10000", label: "6,001 - 10,000 บาท" },
    { value: "10001-15000", label: "10,001 - 15,000 บาท" },
    { value: "15001-", label: "มากกว่า 15,000 บาท" },
];

// จำนวนการ์ดต่อหน้า — หารลงตัวกับกริด 2/3/4 คอลัมน์ แถวสุดท้ายจึงไม่แหว่ง
const PAGE_SIZE = 12;

const EMPTY_FILTERS = { project_id: "", listing_type: "", budget: "", data_search: "", status: "", sort: "" };

// ชื่อ key ใน URL ↔ ชื่อใน state (data_search สั้นลงเป็น q ให้ลิงก์อ่านง่าย)
const URL_KEYS = { project_id: "project", listing_type: "listing_type", budget: "budget", data_search: "q", status: "status", sort: "sort" };

const filtersFromSearch = (search) => {
    const params = new URLSearchParams(search || "");
    const filters = { ...EMPTY_FILTERS };
    Object.entries(URL_KEYS).forEach(([key, urlKey]) => {
        filters[key] = (params.get(urlKey) || "").trim();
    });
    // ลิงก์เก่าที่ส่ง ?project_id= มายังต้องใช้ได้
    if (!filters.project_id) filters.project_id = (params.get("project_id") || "").trim();
    return filters;
};

const searchFromFilters = (filters) => {
    const params = new URLSearchParams();
    Object.entries(URL_KEYS).forEach(([key, urlKey]) => {
        if (filters[key]) params.set(urlKey, filters[key]);
    });
    const qs = params.toString();
    return qs ? `?${qs}` : "";
};

const currentSearch = (props) => (props.location ? props.location.search : window.location.search);

export default class HomePage extends Component {
    state = {
        project: null,          // โครงการที่เลือกอยู่ (null = ดูรวมทุกโครงการ)
        projects: [],           // โครงการที่มีห้องเปิดประกาศ
        projectsLoaded: false,
        listings: [],
        count: 0,
        page: 1,
        hasMore: false,
        content: {},
        setting: {},           // ข้อมูลบริษัท + ช่องทางติดต่อ (ของโครงการที่เลือก · ไม่เลือก = ว่าง)

        filters: filtersFromSearch(currentSearch(this.props)),

        isLoading: true,
        loadingMore: false,
        errorMessage: "",

        inquiryOpen: false,
        inquiryListing: null,

        showTop: false,
    };

    // เลขคำขอล่าสุด — ผลของคำขอเก่าที่ตอบกลับมาช้ากว่า ทิ้งไป ไม่ให้ทับผลของตัวกรองใหม่
    requestId = 0;
    // query string ที่หน้านี้เขียนลง URL เอง — ใช้แยกกับการเปลี่ยน URL จากภายนอก (back/forward)
    lastSearch = null;
    // เนื้อหาหน้าเว็บ (page-content) ของโครงการไหนที่โหลดไว้แล้ว — ไม่เปลี่ยนโครงการก็ไม่ต้องยิงซ้ำ
    contentProjectId = null;

    componentDidMount() {
        loading(false);
        window.scrollTo(0, 0);
        window.addEventListener("scroll", this.onScroll, { passive: true });
        this.loadAll();
    }

    componentDidUpdate(prevProps) {
        // URL เปลี่ยนจากภายนอก (กด back/forward) — อ่านตัวกรองใหม่จาก URL แล้วค้นหาตาม
        const search = currentSearch(this.props);
        if (prevProps.location && search !== currentSearch(prevProps) && search !== this.lastSearch) {
            this.setState({ filters: filtersFromSearch(search) }, () => this.search({ syncUrl: false }));
        }
    }

    componentWillUnmount() {
        window.removeEventListener("scroll", this.onScroll);
        this.requestId++; // ผลที่ยังค้างอยู่ห้าม setState หลัง unmount
    }

    onScroll = () => {
        const showTop = window.scrollY > 640;
        if (showTop !== this.state.showTop) this.setState({ showTop });
    };

    // ---------- โหลดข้อมูล ----------

    buildParams = (page = 1) => {
        const { filters } = this.state;
        const [min, max] = String(filters.budget || "").split("-");
        return {
            // ว่าง = ทุกโครงการ (sale-api ตัด key ที่ค่าว่างทิ้งก่อนส่ง)
            project_id: filters.project_id,
            // ★ ขอทีละหน้า — page=0 คือขอทั้งหมดพร้อมรูปทุกห้อง (ช้ามากเมื่อห้องเยอะ)
            page,
            page_size: PAGE_SIZE,
            listing_type: filters.listing_type,
            min_price: min || "",
            max_price: max || "",
            // ซ่อนช่องค้นหาเลขห้อง ตามคำสั่งผู้ใช้ 19 ต.ค. 2569 — เมินค่า ?q= ที่ติดมาใน URL (เปิดกลับ: คืนเป็น filters.data_search)
            // data_search: filters.data_search,
            data_search: "",
            status: filters.status,
            sort: filters.sort,
        };
    };

    // has_next มาจาก backend (K13) · backend รุ่นเก่าไม่มี = เทียบจำนวนที่โหลดแล้วกับ count แทน
    hasMore = (data, loadedCount) => (typeof data.has_next === "boolean" ? data.has_next : loadedCount < (data.count || 0));

    syncUrl = () => {
        const search = searchFromFilters(this.state.filters);
        if (search === currentSearch(this.props)) return;
        this.lastSearch = search;
        if (this.props.navigate) this.props.navigate({ pathname: "/overview", search }, { replace: true });
        else window.history.replaceState(null, "", `/overview${search}`);
    };

    setTitle = (project) => {
        const name = stripJuristic(project?.project_name);
        document.title = name ? `ห้องประกาศขาย/ให้เช่า ${name}` : "ห้องประกาศขาย/ให้เช่า ทุกโครงการ";
    };

    loadAll = async () => {
        const id = ++this.requestId;
        const projectId = this.state.filters.project_id;
        this.setState({ isLoading: true, errorMessage: "" });

        const [projectRes, listingRes, contentRes] = await Promise.all([
            fetchProjects(),
            fetchListings(this.buildParams(1)),
            fetchPageContent(projectId),
        ]);
        if (id !== this.requestId) return;

        // ★ รายชื่อโครงการตั้งก่อนเสมอ แม้ห้องจะโหลดไม่ได้ — หน้า error ยังต้องมีตัวเลือก/ปุ่มกลับไปดูทุกโครงการ
        const base = {
            projects: projectRes.ok ? projectRes.data || [] : [],
            projectsLoaded: projectRes.ok,
            content: contentRes.ok ? contentRes.data.data || {} : {},
            setting: contentRes.ok ? contentRes.data.setting || {} : {},
        };
        if (contentRes.ok) this.contentProjectId = projectId;

        if (!listingRes.ok) {
            this.setState({ ...base, project: null, listings: [], count: 0, hasMore: false, isLoading: false, errorMessage: listingRes.message || "ไม่พบข้อมูลห้อง" });
            this.setTitle(null);
            return;
        }

        const rows = listingRes.data.data;
        this.setState({
            ...base,
            project: listingRes.data.project,
            listings: rows,
            count: listingRes.data.count,
            page: 1,
            hasMore: this.hasMore(listingRes.data, rows.length),
            isLoading: false,
        });
        this.setTitle(listingRes.data.project);
    };

    search = async ({ syncUrl = true } = {}) => {
        const id = ++this.requestId;
        const projectId = this.state.filters.project_id;
        if (syncUrl) this.syncUrl();
        this.setState({ isLoading: true, errorMessage: "" });

        // เนื้อหาหน้าเว็บผูกกับโครงการ — ยิงใหม่เฉพาะตอนเปลี่ยนโครงการ (เปลี่ยนราคา/สถานะไม่ต้องโหลดซ้ำ)
        const needContent = projectId !== this.contentProjectId;
        const [result, contentRes] = await Promise.all([
            fetchListings(this.buildParams(1)),
            needContent ? fetchPageContent(projectId) : Promise.resolve(null),
        ]);
        if (id !== this.requestId) return;

        const next = {
            isLoading: false,
            page: 1,
            // ★ error ต้องส่งไปแสดง — เดิมกลืนหายแล้วขึ้นว่า "ยังไม่พบห้อง" ทั้งที่จริงคือโหลดไม่สำเร็จ
            errorMessage: result.ok ? "" : result.message || "โหลดข้อมูลห้องไม่สำเร็จ",
            project: result.ok ? result.data.project : null,
            listings: result.ok ? result.data.data : [],
            count: result.ok ? result.data.count : 0,
            hasMore: result.ok ? this.hasMore(result.data, result.data.data.length) : false,
        };
        if (contentRes) {
            next.content = contentRes.ok ? contentRes.data.data || {} : {};
            next.setting = contentRes.ok ? contentRes.data.setting || {} : {};
            this.contentProjectId = contentRes.ok ? projectId : null;
        }
        this.setState(next);
        if (result.ok) this.setTitle(result.data.project);
    };

    loadMore = async () => {
        const { page, loadingMore, hasMore } = this.state;
        if (loadingMore || !hasMore) return;
        const id = this.requestId; // ไม่เพิ่มเลข — ถ้ามีการค้นหาใหม่ระหว่างนี้ ผลโหลดเพิ่มชุดนี้ต้องถูกทิ้ง
        this.setState({ loadingMore: true });

        const result = await fetchListings(this.buildParams(page + 1));
        if (id !== this.requestId) return;

        if (!result.ok) {
            this.setState({ loadingMore: false, errorMessage: result.message || "โหลดห้องเพิ่มไม่สำเร็จ" });
            return;
        }
        this.setState((prev) => {
            // กันห้องซ้ำกรณีมีห้องเข้า/ออกระหว่างเลื่อนดู (offset เลื่อน)
            const known = new Set(prev.listings.map((i) => i.uid));
            const listings = [...prev.listings, ...result.data.data.filter((i) => !known.has(i.uid))];
            return {
                listings,
                page: page + 1,
                count: result.data.count || prev.count,
                hasMore: this.hasMore(result.data, listings.length),
                loadingMore: false,
                errorMessage: "",
            };
        });
    };

    handleFilterChange = (key, value) => {
        this.setState(
            (prev) => {
                const filters = { ...prev.filters, [key]: value };
                // เปลี่ยนประเภทประกาศแล้วช่วงราคาเดิมใช้ไม่ได้ (คนละหลัก) ต้องล้างทิ้ง
                // สถานะ "ขายแล้ว" ไม่มีในประกาศเช่า (และกลับกัน) ล้างด้วย
                if (key === "listing_type") {
                    filters.budget = "";
                    if (["ขายแล้ว", "ให้เช่าแล้ว"].includes(filters.status)) filters.status = "";
                    if (value === "เช่า" && /^price_/.test(filters.sort)) filters.sort = "";
                    if (value === "ขาย" && /^rent_/.test(filters.sort)) filters.sort = "";
                }
                return { filters };
            },
            // ช่องพิมพ์เลขห้องรอกด Enter/ปุ่มค้นหา · ที่เหลือค้นหาทันที (และเขียนลง URL)
            key === "data_search" ? undefined : () => this.search()
        );
    };

    clearFilters = () => {
        this.setState({ filters: { ...EMPTY_FILTERS } }, () => this.search());
    };

    // ทางลัดค้นหา — link_url เก็บเงื่อนไขเป็น query string เช่น "listing_type=เช่า"
    // ค่าที่ไม่รู้จักถูกทิ้ง ไม่ยัดลง state มั่ว
    shortcut = (item) => {
        const params = new URLSearchParams(item.link_url || "");
        this.setState(
            (prev) => ({
                filters: {
                    ...prev.filters,
                    listing_type: params.get("listing_type") || "",
                    budget: params.get("budget") || "",
                    data_search: params.get("data_search") || "",
                    status: params.get("status") || "",
                    sort: params.get("sort") || "",
                },
            }),
            () => {
                this.search();
                this.scrollToListings();
            }
        );
    };

    scrollToListings = () => {
        const target = document.querySelector("#hm-featured");
        if (target) window.scrollTo({ top: target.offsetTop - 90, behavior: "smooth" });
    };

    // ---------- การกระทำ ----------

    openRoom = (room) => {
        // ใช้โครงการของห้องนั้นเอง ไม่ใช่ตัวกรองที่เลือกอยู่
        const url = `/room?project=${encodeURIComponent(room.project_id || "")}&uid=${encodeURIComponent(room.uid)}`;
        // ★ เปลี่ยนหน้าแบบ SPA — กด back แล้วกลับมาที่ /overview พร้อมตัวกรองใน URL
        if (this.props.navigate) this.props.navigate(url);
        else window.location.href = url;
    };

    openProject = (project) => {
        this.handleFilterChange("project_id", project.project_id);
        this.scrollToListings();
    };

    openInquiry = (listing) => this.setState({ inquiryOpen: true, inquiryListing: listing || null });

    sendInquiry = async (payload) => submitInquiry(payload);

    render() {
        const s = this.state;
        const content = s.content || {};
        const budgets = s.filters.listing_type === "เช่า" ? RENT_BUDGETS : SALE_BUDGETS;

        const navbar = (
            <HomeNavbar
                brand={s.setting.company_name_th || (s.project ? stripJuristic(s.project.project_name) : "ห้องประกาศขาย/ให้เช่า")}
                logo={s.setting.logo_url}
                onContact={() => this.openInquiry(null)}
            />
        );

        const inquiry = (
            <HomeInquiry
                open={s.inquiryOpen}
                listing={s.inquiryListing}
                project={s.project}
                projects={s.projects}
                onClose={() => this.setState({ inquiryOpen: false })}
                onSubmit={this.sendInquiry}
            />
        );

        // ★ project ใน URL ไม่มีอยู่จริง (ลิงก์เก่า/พิมพ์ผิด) — ยังมีเมนูบนและปุ่มพาไปดูทุกโครงการ
        //   เดิมเป็นหน้าโล่ง ๆ ไม่มี navbar ผู้ใช้ไม่มีทางไปต่อนอกจากแก้ URL เอง
        const invalidProject =
            !s.isLoading &&
            !!s.errorMessage &&
            !!s.filters.project_id &&
            !s.listings.length &&
            // รายชื่อโครงการโหลดไม่ได้ (เน็ตหลุด) ≠ โครงการไม่มีจริง — กรณีนั้นไปแสดง error + ปุ่มลองใหม่ในหน้าปกติ
            s.projectsLoaded &&
            !s.projects.some((p) => p.project_id === s.filters.project_id);

        if (invalidProject) {
            return (
                <div className="mn-home">
                    {navbar}
                    <div className="hm-container py-5">
                        <div className="hm-soon" role="alert">
                            <span className="hm-soon__icon hm-ico">{ICON.info}</span>
                            <div className="hm-soon__title">ไม่พบโครงการที่ต้องการ</div>
                            <div className="hm-soon__desc">{s.errorMessage} — ลิงก์อาจเก่าหรือโครงการนี้ปิดประกาศแล้ว</div>
                            <button type="button" className="hm-btn hm-btn--primary hm-btn--sm mt-3" onClick={this.clearFilters}>
                                ดูทุกโครงการ
                            </button>
                        </div>
                    </div>
                    <HomeFooter setting={s.setting} projects={s.projects} />
                    {inquiry}
                </div>
            );
        }

        // ★ ภาพพื้นหลังใช้เป็นฉากหลังของ "แถบบนสุด" เท่านั้น ไม่ใช่ทั้งหน้า
        //   ปูทั้งหน้าแล้วเนื้อหาด้านล่าง (การ์ดห้อง ตาราง ฟอร์ม) อ่านยาก
        //   ต้องคลุมฝ้าหนามากจนภาพแทบไม่เหลือ สู้ใส่เฉพาะด้านบนไม่ได้
        //   หลายใบ = เล่นวนเป็นสไลด์ · ใบเดียว = ภาพนิ่ง · ไม่มีเลย = พื้นขาว
        const backgrounds = (content.background || []).map((i) => i.image_url).filter(Boolean);

        return (
            <div className="mn-home">
                {navbar}

                <HomeHero
                    slides={backgrounds}
                    hero={(content.hero || [])[0]}
                    filters={s.filters}
                    budgets={budgets}
                    projects={s.projects}
                    onChange={this.handleFilterChange}
                    onSearch={() => this.search()}
                />

                <HomeCategories items={content.shortcut || []} onSelect={this.shortcut} />

                <HomePromo
                    banners={content.banner || []}
                    side={(content.contact || [])[0]}
                    onContact={() => this.openInquiry(null)}
                />

                <HomeFeatured
                    items={s.listings}
                    count={s.count}
                    loading={s.isLoading}
                    loadingMore={s.loadingMore}
                    hasMore={s.hasMore}
                    error={s.errorMessage}
                    filters={s.filters}
                    title={s.project ? `ห้องประกาศ ${stripJuristic(s.project.project_name)}` : "ห้องประกาศขายและให้เช่า ทุกโครงการ"}
                    onFilterChange={this.handleFilterChange}
                    onLoadMore={this.loadMore}
                    onRetry={() => this.search()}
                    onOpen={this.openRoom}
                    onInquiry={this.openInquiry}
                    onClear={this.clearFilters}
                />

                <HomeProjects
                    items={s.projects}
                    testimonials={content.testimonial || []}
                    onOpen={this.openProject}
                />

                {/* ★ "โครงการนี้" ใช้ได้เฉพาะตอนเลือกโครงการอยู่ · ดูรวมทุกโครงการใช้หัวข้อกลางของคอมโพเนนต์ */}
                <HomeAbout
                    items={content.highlight || []}
                    stats={content.stat || []}
                    title={s.project ? "ทำไมต้องเลือกโครงการนี้" : ""}
                />

                <HomeFaq items={content.faq || []} />

                <HomeCta
                    content={(content.contact || [])[0]}
                    setting={s.setting}
                    project={s.project}
                    projects={s.projects}
                    onContact={() => this.openInquiry(null)}
                />

                <HomeFooter
                    content={(content.footer || [])[0]}
                    setting={s.setting}
                    socials={content.social || []}
                    projects={s.projects}
                    faqs={content.faq || []}
                />

                {inquiry}

                {s.showTop && (
                    <button
                        type="button"
                        className="hm-to-top"
                        aria-label="กลับขึ้นบน"
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    >
                        <span className="hm-ico">{ICON.chevron_up}</span>
                    </button>
                )}
            </div>
        );
    }
}
